import {Fragment, useEffect, useRef, useState} from "react"

import {useLocalize} from "@agenta/i18n"
import {Check} from "lucide-react"

import {cn} from "../components/ui/utils"

import type {FilterMenuOption} from "./types"

/**
 * The option list behind every flyout — and, on its own, the whole of {@link GroupMenu}.
 *
 * Owns its roving focus rather than leaning on Radix's typeahead: the panel above it carries a
 * search input, and a menu's typeahead steals the letters that input needs.
 */
export const FilterMenuOptionList = ({
    options,
    selected,
    emptyText: emptyTextProp,
    autoFocus = true,
    onSelect,
    onDismiss,
    className,
}: {
    options: FilterMenuOption[]
    /** Every currently-checked value; a single-select row passes one. */
    selected: string[]
    emptyText?: string
    autoFocus?: boolean
    onSelect: (value: string) => void
    /** Left / Escape — the flyout's caller decides what closing means. */
    onDismiss?: () => void
    className?: string
}) => {
    const t = useLocalize()
    const emptyText =
        emptyTextProp === undefined ? t("com_core_nothing_to_choose_from") : emptyTextProp
    const refs = useRef<(HTMLButtonElement | null)[]>([])
    const firstEnabled = options.findIndex((option) => !option.disabled)
    const [active, setActive] = useState(() => (firstEnabled === -1 ? 0 : firstEnabled))
    // The panel's search narrows `options` while this list stays mounted, so a stored index can
    // outlive the option it pointed at. Rendering off the clamped one keeps a tabbable member in
    // the list; without it a shrunk list has no `tabIndex={0}` until an arrow key is pressed.
    const activeIndex = active < options.length ? active : firstEnabled === -1 ? 0 : firstEnabled

    useEffect(() => {
        if (!autoFocus) return
        // Focus follows the active index, so navigation is never yanked back to the top.
        refs.current[activeIndex]?.focus()
    }, [activeIndex, autoFocus])

    const move = (delta: number) => {
        if (!options.length) return
        let next = activeIndex
        let steps = options.length
        while (steps > 0) {
            next = (next + delta + options.length) % options.length
            if (!options[next]?.disabled) break
            steps -= 1
        }
        setActive(next)
    }

    if (!options.length) {
        return (
            <p
                className={cn(
                    "m-0 px-3 py-6 text-center text-[12px] text-muted-foreground",
                    className,
                )}
            >
                {emptyText}
            </p>
        )
    }

    return (
        <div
            role="listbox"
            aria-orientation="vertical"
            className={cn("flex flex-col", className)}
            onKeyDown={(event) => {
                if (event.key === "ArrowDown") {
                    event.preventDefault()
                    move(1)
                } else if (event.key === "ArrowUp") {
                    event.preventDefault()
                    move(-1)
                } else if (event.key === "ArrowLeft") {
                    event.preventDefault()
                    onDismiss?.()
                }
            }}
        >
            {options.map((option, index) => {
                const checked = selected.includes(option.value)
                const heading =
                    option.group && option.group !== options[index - 1]?.group ? (
                        <div
                            role="presentation"
                            className="truncate px-2 pb-1 pt-2 text-[11px] font-medium text-muted-foreground first:pt-1"
                        >
                            {option.group}
                        </div>
                    ) : null
                return (
                    <Fragment key={option.value}>
                        {heading}
                        <button
                            ref={(node) => {
                                refs.current[index] = node
                            }}
                            type="button"
                            role="option"
                            aria-selected={checked}
                            disabled={option.disabled}
                            tabIndex={index === activeIndex ? 0 : -1}
                            onFocus={() => setActive(index)}
                            onClick={() => onSelect(option.value)}
                            className={cn(
                                // Preflight is off app-wide (antd ships its own reset), so a bare
                                // <button> needs the resets restated.
                                "box-border cursor-pointer appearance-none border-0 bg-transparent font-[inherit]",
                                "flex w-full items-center gap-2 rounded-control-sm px-2 py-1.5 text-left",
                                "text-[13px] text-foreground outline-none transition-colors",
                                "hover:bg-accent focus-visible:bg-accent",
                                "disabled:cursor-not-allowed disabled:text-disabled disabled:hover:bg-transparent",
                            )}
                        >
                            {option.icon ? (
                                <span className="flex size-4 shrink-0 items-center justify-center text-muted-foreground">
                                    {option.icon}
                                </span>
                            ) : null}
                            <span className="min-w-0 flex-1 truncate" title={option.label}>
                                {option.label}
                            </span>
                            {/* The check keeps its box whether or not it is drawn, so the label
                            column never shifts as the selection moves. */}
                            <span className="flex size-4 shrink-0 items-center justify-center">
                                {checked ? <Check size={14} aria-hidden /> : null}
                            </span>
                        </button>
                    </Fragment>
                )
            })}
        </div>
    )
}
